const SCC_MEDIA_TRANSITION_DURATION = 620;
const SCC_MEDIA_PREVIEW_DELAY = 460;
const SCC_MEDIA_PREVIEW_INTERVAL = 1500;

class SccMediaGallery extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sccReady === 'true') return;
    this.dataset.sccReady = 'true';
    this.currentIndex = Math.max(
      0,
      this.items.findIndex((item) => !item.hidden),
    );
    this.targetIndex = this.currentIndex;
    this.addEventListener('click', this.handleClick);
    this.addEventListener('pointerdown', this.handlePointerDown);
    this.addEventListener('pointerup', this.handlePointerUp);
    this.addEventListener('pointercancel', this.resetPointerGesture);
    this.mediaLink = this.querySelector('.scc-product-card__media-link');
    this.mediaLink?.addEventListener('pointerenter', this.handleMediaPointerEnter);
    this.mediaLink?.addEventListener('pointerleave', this.handleMediaPointerLeave);
    this.syncProductVariant(this.items[this.currentIndex]);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('pointerdown', this.handlePointerDown);
    this.removeEventListener('pointerup', this.handlePointerUp);
    this.removeEventListener('pointercancel', this.resetPointerGesture);
    this.mediaLink?.removeEventListener('pointerenter', this.handleMediaPointerEnter);
    this.mediaLink?.removeEventListener('pointerleave', this.handleMediaPointerLeave);
    this.stopMediaPreview();
    clearTimeout(this.suppressClickTimer);
    clearTimeout(this.mediaTransitionTimer);
  }

  get items() {
    return Array.from(this.querySelectorAll('[data-scc-media-item]'));
  }

  get controls() {
    return Array.from(this.querySelectorAll('[data-scc-media-index]'));
  }

  handleClick = (event) => {
    const mediaLink = event.target.closest?.('.scc-product-card__media-link');
    if (this.suppressMediaClick && mediaLink && this.contains(mediaLink)) {
      event.preventDefault();
      event.stopPropagation();
      this.suppressMediaClick = false;
      clearTimeout(this.suppressClickTimer);
      return;
    }

    const directControl = event.target.closest('[data-scc-media-index]');
    if (directControl && this.contains(directControl)) {
      this.markManualSelection();
      this.show(Number(directControl.dataset.sccMediaIndex));
      return;
    }

    const previous = event.target.closest('[data-scc-media-previous]');
    if (previous && this.contains(previous)) {
      this.markManualSelection();
      this.step(-1);
      return;
    }

    const next = event.target.closest('[data-scc-media-next]');
    if (next && this.contains(next)) {
      this.markManualSelection();
      this.step(1);
    }
  };

  handlePointerDown = (event) => {
    if (event.isPrimary === false || !['touch', 'pen'].includes(event.pointerType) || this.items.length < 2) return;

    const mediaLink = event.target.closest?.('.scc-product-card__media-link');
    if (!mediaLink || !this.contains(mediaLink)) return;
    if (this.closest('[data-scc-horizontal-product-rail]')) return;

    this.pointerGesture = {
      id: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };
    mediaLink.setPointerCapture?.(event.pointerId);
  };

  handlePointerUp = (event) => {
    const gesture = this.pointerGesture;
    if (!gesture || gesture.id !== event.pointerId) return;

    this.resetPointerGesture();
    const distanceX = event.clientX - gesture.startX;
    const distanceY = event.clientY - gesture.startY;
    const horizontalDistance = Math.abs(distanceX);
    const verticalDistance = Math.abs(distanceY);

    if (horizontalDistance < 44 || horizontalDistance <= verticalDistance * 1.25) return;

    event.preventDefault?.();
    this.suppressMediaClick = true;
    this.markManualSelection();
    clearTimeout(this.suppressClickTimer);
    this.suppressClickTimer = setTimeout(() => {
      this.suppressMediaClick = false;
    }, 0);
    this.step(distanceX < 0 ? 1 : -1);
  };

  resetPointerGesture = () => {
    this.pointerGesture = null;
  };

  handleMediaPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    if (this.manualSelectionActive) return;

    const hoverIndex = Number(this.dataset.sccHoverMediaIndex);
    if (!Number.isInteger(hoverIndex) || hoverIndex < 0 || hoverIndex >= this.items.length) return;

    this.hoverReturnIndex = this.targetIndex ?? this.currentIndex;
    const reducedMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (reducedMotion || this.items.length < 3) {
      this.show(hoverIndex);
      return;
    }

    this.mediaPreviewActive = true;
    this.mediaPreviewNextIndex = hoverIndex;
    this.dataset.sccPreviewing = 'true';
    clearTimeout(this.mediaPreviewTimer);
    this.mediaPreviewTimer = setTimeout(this.advanceMediaPreview, SCC_MEDIA_PREVIEW_DELAY);
  };

  handleMediaPointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.manualSelectionActive = false;
    if (this.hoverReturnIndex == null) return;

    const returnIndex = this.hoverReturnIndex;
    this.hoverReturnIndex = null;
    this.stopMediaPreview();
    this.show(returnIndex);
  };

  markManualSelection() {
    this.manualSelectionActive = true;
    this.hoverReturnIndex = null;
    this.stopMediaPreview();
  }

  advanceMediaPreview = async () => {
    if (!this.mediaPreviewActive) return;

    const nextIndex = this.mediaPreviewNextIndex;
    this.mediaPreviewNextIndex = (nextIndex + 1) % this.items.length;
    const requestedIndex = nextIndex === 0 && (this.targetIndex ?? this.currentIndex) === this.items.length - 1
      ? this.items.length
      : nextIndex;
    await this.show(requestedIndex);

    if (!this.mediaPreviewActive) return;
    this.mediaPreviewTimer = setTimeout(this.advanceMediaPreview, SCC_MEDIA_PREVIEW_INTERVAL);
  };

  stopMediaPreview() {
    this.mediaPreviewActive = false;
    clearTimeout(this.mediaPreviewTimer);
    this.mediaPreviewTimer = undefined;
    delete this.dataset.sccPreviewing;
  }

  syncProductVariant(item) {
    // Preserve canonical product.url on cards so size is never prematurely preselected on navigation
    return;
  }

  step(direction) {
    this.show((this.targetIndex ?? this.currentIndex) + direction);
  }

  async show(requestedIndex) {
    const items = this.items;
    if (!items.length) return;

    if (!Number.isFinite(requestedIndex)) return;
    const originIndex = this.targetIndex ?? this.currentIndex;
    const index = ((requestedIndex % items.length) + items.length) % items.length;
    const requestedItem = items[index];
    if (!requestedItem) return;
    const requestedImage = requestedItem.querySelector('img');
    const requestToken = (this.requestToken || 0) + 1;
    this.requestToken = requestToken;
    this.targetIndex = index;
    this.dataset.sccDirection = requestedIndex < originIndex ? 'previous' : 'next';

    if (requestedImage && !requestedImage.complete) {
      requestedImage.loading = 'eager';
      try {
        await requestedImage.decode();
      } catch {
        // The image element still reports its own fallback state if decoding fails.
      }
    }

    if (requestToken !== this.requestToken) return;
    const previousIndex = this.currentIndex;
    const previousItem = items[previousIndex];
    const reducedMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const animateTransition = previousIndex !== index && !reducedMotion;
    this.currentIndex = index;
    clearTimeout(this.mediaTransitionTimer);

    items.forEach((item, itemIndex) => {
      const selected = itemIndex === index;
      const leaving = animateTransition && itemIndex === previousIndex;
      item.hidden = !selected && !leaving;
      item.classList.toggle('is-active', selected);
      item.classList.toggle('is-leaving', leaving);
    });

    if (animateTransition && previousItem) {
      this.mediaTransitionTimer = setTimeout(() => {
        if (previousItem !== items[this.currentIndex]) previousItem.hidden = true;
        previousItem.classList.remove('is-leaving');
        this.mediaTransitionTimer = undefined;
      }, SCC_MEDIA_TRANSITION_DURATION);
    }

    this.controls.forEach((control) => {
      control.setAttribute(
        'aria-pressed',
        String(Number(control.dataset.sccMediaIndex) === index),
      );
    });

    const position = this.querySelector('[data-scc-media-position]');
    if (position) {
      position.textContent = `${String(index + 1).padStart(2, '0')} / ${String(items.length).padStart(2, '0')}`;
    }

    const label = this.querySelector('[data-scc-media-current-label]');
    if (label) {
      label.textContent = items[index].dataset.sccMediaLabel || '';
    }

    this.syncProductVariant(items[index]);

    const nextImage = items[(index + 1) % items.length]?.querySelector('img');
    if (nextImage && !nextImage.complete) {
      nextImage.loading = 'eager';
      nextImage.decode().catch(() => {});
    }
  }
}

if (!customElements.get('scc-media-gallery')) {
  customElements.define('scc-media-gallery', SccMediaGallery);
}

class SccCampaignCarousel extends HTMLElement {
  connectedCallback() {
    if (this.sccConnected) return;
    this.sccConnected = true;
    this.dataset.sccReady = 'true';
    this.slides = Array.from(this.querySelectorAll('[data-scc-carousel-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-scc-carousel-index]'));
    this.track = this.querySelector('[data-scc-carousel-track]');
    this.pauseButton = this.querySelector('[data-scc-carousel-pause]');
    this.heading = this.querySelector('[data-scc-carousel-heading]');
    this.primaryLink = this.querySelector('[data-scc-carousel-primary]');
    this.currentIndex = Math.max(
      0,
      this.slides.findIndex((slide) => slide.classList.contains('is-active')),
    );
    this.interval = Math.max(5000, Number(this.dataset.interval) || 7000);
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.isIntersecting = true;

    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeyDown);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    this.track?.addEventListener('pointerdown', this.handlePointerDown);
    this.track?.addEventListener('pointerup', this.handlePointerUp);
    this.track?.addEventListener('pointercancel', this.resetPointerGesture);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.motionQuery.addEventListener?.('change', this.handleMotionChange);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.handleIntersection, {
        threshold: 0.25,
      });
      this.observer.observe(this);
    }

    this.userHasInteracted = false;
    const startAutoplayOnInteraction = () => {
      if (this.userHasInteracted) return;
      this.userHasInteracted = true;
      ['scroll', 'touchstart', 'pointerdown', 'keydown'].forEach((ev) => {
        window.removeEventListener(ev, startAutoplayOnInteraction, { passive: true });
      });
      this.scheduleAutoplay();
    };
    ['scroll', 'touchstart', 'pointerdown', 'keydown'].forEach((ev) => {
      window.addEventListener(ev, startAutoplayOnInteraction, { passive: true, once: true });
    });

    this.show(this.currentIndex, 'initial');
  }

  disconnectedCallback() {
    this.sccConnected = false;
    clearTimeout(this.autoplayTimer);
    clearTimeout(this.preloadTimer);
    clearTimeout(this.suppressClickTimer);
    cancelAnimationFrame(this.progressFrame);
    this.headingAnimation?.cancel();
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeyDown);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.track?.removeEventListener('pointerdown', this.handlePointerDown);
    this.track?.removeEventListener('pointerup', this.handlePointerUp);
    this.track?.removeEventListener('pointercancel', this.resetPointerGesture);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.motionQuery?.removeEventListener?.('change', this.handleMotionChange);
    this.observer?.disconnect();
  }

  handleClick = (event) => {
    const mediaLink = event.target.closest?.('.scc-floor-hero__media');
    if (this.suppressMediaClick && mediaLink && this.contains(mediaLink)) {
      event.preventDefault();
      event.stopPropagation();
      this.suppressMediaClick = false;
      return;
    }

    const indexButton = event.target.closest?.('[data-scc-carousel-index]');
    if (indexButton && this.contains(indexButton)) {
      this.show(Number(indexButton.dataset.sccCarouselIndex), 'manual');
      return;
    }

    if (event.target.closest?.('[data-scc-carousel-previous]')) {
      this.show(this.currentIndex - 1, 'manual');
      return;
    }

    if (event.target.closest?.('[data-scc-carousel-next]')) {
      this.show(this.currentIndex + 1, 'manual');
      return;
    }

    if (event.target.closest?.('[data-scc-carousel-pause]')) {
      this.userPaused = !this.userPaused;
      this.syncPauseButton();
      this.scheduleAutoplay();
    }
  };

  handleKeyDown = (event) => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    if (event.target instanceof HTMLElement && event.target.matches('input, textarea, select, [contenteditable]')) {
      return;
    }

    event.preventDefault();
    this.show(this.currentIndex + (event.key === 'ArrowRight' ? 1 : -1), 'manual');
  };

  handleFocusIn = () => {
    this.focusPaused = true;
    this.scheduleAutoplay();
  };

  handleFocusOut = (event) => {
    if (event.relatedTarget && this.contains(event.relatedTarget)) return;
    this.focusPaused = false;
    this.scheduleAutoplay();
  };

  handleVisibilityChange = () => {
    this.scheduleAutoplay();
  };

  handleMotionChange = () => {
    this.scheduleAutoplay();
  };

  handleIntersection = (entries) => {
    this.isIntersecting = entries[0]?.isIntersecting ?? true;
    this.scheduleAutoplay();
  };

  handlePointerDown = (event) => {
    if (
      event.isPrimary === false ||
      !['touch', 'pen'].includes(event.pointerType) ||
      this.slides.length < 2
    ) {
      return;
    }

    this.pointerGesture = {
      id: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };
    this.track?.setPointerCapture?.(event.pointerId);
  };

  handlePointerUp = (event) => {
    const gesture = this.pointerGesture;
    if (!gesture || gesture.id !== event.pointerId) return;

    this.resetPointerGesture();
    const distanceX = event.clientX - gesture.startX;
    const distanceY = event.clientY - gesture.startY;
    if (Math.abs(distanceX) < 48 || Math.abs(distanceX) <= Math.abs(distanceY) * 1.2) return;

    event.preventDefault?.();
    this.suppressMediaClick = true;
    clearTimeout(this.suppressClickTimer);
    this.suppressClickTimer = setTimeout(() => {
      this.suppressMediaClick = false;
    }, 350);
    this.show(this.currentIndex + (distanceX < 0 ? 1 : -1), 'manual');
  };

  resetPointerGesture = () => {
    this.pointerGesture = null;
  };

  show(requestedIndex, source = 'manual') {
    if (!this.slides.length || !Number.isFinite(requestedIndex)) return;
    const previousIndex = this.currentIndex;
    const index =
      ((requestedIndex % this.slides.length) + this.slides.length) %
      this.slides.length;

    if (source === 'manual') {
      this.userHasInteracted = true;
    }

    if (source !== 'initial') {
      this.dataset.sccDirection =
        requestedIndex < previousIndex ? 'previous' : 'next';
    }

    this.currentIndex = index;
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.toggleAttribute('inert', !active);
    });

    this.dots.forEach((dot) => {
      dot.setAttribute(
        'aria-pressed',
        String(Number(dot.dataset.sccCarouselIndex) === index),
      );
    });

    const current = this.querySelector('[data-scc-carousel-current]');
    if (current) current.textContent = String(index + 1).padStart(2, '0');

    const activeSlide = this.slides[index];
    const slideTitle = activeSlide?.dataset.sccSlideTitle || '';
    const slideUrl = activeSlide?.dataset.sccSlideUrl || '';
    this.dataset.sccActiveAsset = activeSlide?.dataset.sccCampaignAsset || '';
    const title = this.querySelector('[data-scc-carousel-title]');
    if (title) title.textContent = slideTitle;

    if (this.heading && slideTitle) {
      this.heading.textContent = slideTitle;
      if (source !== 'initial' && !this.motionQuery?.matches && this.heading.animate) {
        this.headingAnimation?.cancel();
        this.headingAnimation = this.heading.animate(
          [
            { clipPath: 'inset(0 0 100% 0)', transform: 'translateY(8px)' },
            { clipPath: 'inset(0)', transform: 'translateY(0)' },
          ],
          {
            duration: 440,
            easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
          },
        );
      }
    }

    if (this.primaryLink && slideUrl) this.primaryLink.href = slideUrl;

    clearTimeout(this.preloadTimer);
    const preloadDelay = source === 'initial' ? 7000 : 600;
    this.preloadTimer = setTimeout(() => {
      const nextImage = this.slides[(index + 1) % this.slides.length]?.querySelector('img');
      if (!nextImage || nextImage.complete) return;
      nextImage.loading = 'eager';
      nextImage.fetchPriority = 'low';
      nextImage.decode().catch(() => {});
    }, preloadDelay);

    this.scheduleAutoplay();
  }

  syncPauseButton() {
    this.dataset.sccPaused = String(Boolean(this.userPaused));
    if (!this.pauseButton) return;
    const label = this.userPaused
      ? this.pauseButton.dataset.resumeLabel
      : this.pauseButton.dataset.pauseLabel;
    this.pauseButton.setAttribute('aria-pressed', String(Boolean(this.userPaused)));
    this.pauseButton.setAttribute('aria-label', label || '');
    this.pauseButton.title = label || '';
  }

  scheduleAutoplay() {
    clearTimeout(this.autoplayTimer);
    cancelAnimationFrame(this.progressFrame);
    this.dots.forEach((dot) => dot.classList.remove('is-timing'));
    this.dataset.sccAutoplayRunning = 'false';
    const shouldRun =
      this.dataset.autoplay === 'true' &&
      this.slides.length > 1 &&
      !this.motionQuery?.matches &&
      !this.userPaused &&
      !this.focusPaused &&
      !document.hidden &&
      this.isIntersecting &&
      this.userHasInteracted;

    if (!shouldRun) return;
    this.dataset.sccAutoplayRunning = 'true';
    const activeDot = this.dots.find(
      (dot) => Number(dot.dataset.sccCarouselIndex) === this.currentIndex,
    );
    this.progressFrame = requestAnimationFrame(() => {
      activeDot?.classList.add('is-timing');
    });
    this.autoplayTimer = setTimeout(() => {
      this.show(this.currentIndex + 1, 'autoplay');
    }, this.interval);
  }
}

if (!customElements.get('scc-campaign-carousel')) {
  customElements.define('scc-campaign-carousel', SccCampaignCarousel);
}

class SccArtworkReel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sccReady === 'true') return;
    this.dataset.sccReady = 'true';
    this.section = this.closest('.scc-artwork-reel');
    this.track = this.querySelector('[data-scc-artwork-track]');
    this.previousButton = this.section?.querySelector('[data-scc-reel-previous]');
    this.nextButton = this.section?.querySelector('[data-scc-reel-next]');
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.section?.addEventListener('click', this.handleClick);
    this.track?.addEventListener('scroll', this.handleScroll, { passive: true });
    if ('ResizeObserver' in window && this.track) {
      this.resizeObserver = new ResizeObserver(this.updateControls);
      this.resizeObserver.observe(this.track);
    }
    requestAnimationFrame(this.updateControls);
  }

  disconnectedCallback() {
    this.section?.removeEventListener('click', this.handleClick);
    this.track?.removeEventListener('scroll', this.handleScroll);
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.scrollFrame);
  }

  handleClick = (event) => {
    if (event.target.closest?.('[data-scc-reel-previous]')) {
      this.scroll(-1);
      return;
    }
    if (event.target.closest?.('[data-scc-reel-next]')) this.scroll(1);
  };

  handleScroll = () => {
    cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => {
      this.prepareImages(this.track?.scrollLeft || 0);
      this.updateControls();
    });
  };

  scroll(direction) {
    if (!this.track) return;
    const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const target = Math.max(
      0,
      Math.min(
        maximum,
        this.track.scrollLeft +
          direction * Math.max(260, this.track.clientWidth * 0.82),
      ),
    );
    this.prepareImages(target);
    this.track.scrollTo({
      left: target,
      behavior: this.motionQuery.matches ? 'auto' : 'smooth',
    });
  }

  prepareImages(targetLeft) {
    if (!this.track) return;
    const preloadStart = Math.max(0, targetLeft - this.track.clientWidth * 0.15);
    const preloadEnd = targetLeft + this.track.clientWidth * 1.35;

    // Read phase: calculate layout positions for all items to prevent layout thrashing
    const itemsData = Array.from(this.track.querySelectorAll('.scc-artwork-reel__item')).map((item) => {
      const start = item.offsetLeft;
      return { item, start, end: start + item.offsetWidth };
    });

    // Write phase: mutate DOM without causing interleaved reflows
    itemsData.forEach(({ item, start, end }) => {
      if (end < preloadStart || start > preloadEnd) return;
      const image = item.querySelector('img');
      if (!image || image.dataset.sccPrepared === 'true') return;
      image.dataset.sccPrepared = 'true';
      image.fetchPriority = 'low';
      if (image.complete) image.decode().catch(() => {});
    });
  }

  updateControls = () => {
    if (!this.track) return;
    const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    if (this.previousButton) this.previousButton.disabled = this.track.scrollLeft <= 3;
    if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maximum - 3;
  };
}

if (!customElements.get('scc-artwork-reel')) {
  customElements.define('scc-artwork-reel', SccArtworkReel);
}

document.addEventListener('click', (event) => {
  const openButton = event.target.closest('[data-scc-fixture-open]');
  if (openButton) {
    const dialog = document.getElementById(openButton.dataset.sccFixtureOpen);
    if (dialog && typeof dialog.showModal === 'function') {
      dialog.showModal();
      dialog.querySelector('[data-scc-fixture-close]')?.focus();
      dialog.addEventListener(
        'close',
        () => {
          if (document.contains(openButton)) openButton.focus();
        },
        { once: true },
      );
    }
    return;
  }

  const closeButton = event.target.closest('[data-scc-fixture-close]');
  if (closeButton) {
    closeButton.closest('dialog')?.close();
    return;
  }

  const sizeButton = event.target.closest('[data-scc-fixture-size]');
  if (sizeButton) {
    const sizeGroup = sizeButton.parentElement;
    sizeGroup
      ?.querySelectorAll('[data-scc-fixture-size]')
      .forEach((button) => button.setAttribute('aria-pressed', 'false'));
    sizeButton.setAttribute('aria-pressed', 'true');
    return;
  }

  const filterButton = event.target.closest('[data-scc-filter]');
  if (!filterButton) return;

  const shelf = filterButton.closest('[data-scc-product-shelf]');
  if (!shelf) return;

  const filter = filterButton.dataset.sccFilter;
  shelf
    .querySelectorAll('[data-scc-filter]')
    .forEach((button) =>
      button.setAttribute('aria-pressed', String(button === filterButton)),
    );

  let visibleCount = 0;
  shelf.querySelectorAll('[data-scc-product-card]').forEach((card) => {
    const visible =
      filter === 'all' ||
      (filter === 'regular' && card.dataset.fit === 'regular') ||
      (filter === 'oversized' && card.dataset.fit === 'oversized') ||
      (filter === 'back' && card.dataset.backPrint === 'true');
    card.hidden = !visible;
    if (visible) visibleCount += 1;
  });

  const status = shelf.querySelector('[data-scc-filter-status]');
  if (status) {
    status.textContent = status.dataset.sccStatusTemplate.replace(
      '__COUNT__',
      String(visibleCount),
    );
  }
});

document.addEventListener('click', (event) => {
  if (event.target.matches('dialog.scc-fixture-product')) {
    event.target.close();
  }
});
